import {
  classifyXeroFailure,
  xeroCredentialsFromEnv,
  xeroEnv,
  xeroOrganisationName,
  type XeroCredentials,
  type XeroProblem,
} from '@/features/local-mcp/xero-setup';
import {
  useServerConnectionTestQuery,
  useUpdateMcpServerMutation,
} from '@/features/settings/sub-settings/settings-local-mcp.query';

import type { ServerEntry } from '@littlebird/axon-mcp';

export type XeroStatus =
  | { kind: 'checking' }
  | { kind: 'connected'; organisationName: string | undefined }
  | { kind: 'failed'; problem: XeroProblem };

export function useXeroConfiguration(server: ServerEntry) {
  const update = useUpdateMcpServerMutation();
  const testQuery = useServerConnectionTestQuery(server);
  const { transport } = server;
  const credentials = xeroCredentialsFromEnv(
    transport.kind === 'http' ? undefined : transport.env
  );

  const status: XeroStatus = testQuery.isFetching
    ? { kind: 'checking' }
    : testQuery.data?.ok
      ? {
          kind: 'connected',
          organisationName: xeroOrganisationName(testQuery.data),
        }
      : {
          kind: 'failed',
          problem: testQuery.data
            ? classifyXeroFailure(testQuery.data)
            : 'connection_failed',
        };

  // The server restarts with the new env, and the changed transport re-runs
  // the connection test
  const saveCredentials = (
    changes: Partial<XeroCredentials>,
    onSaved: () => void
  ) => {
    if (transport.kind === 'http') return;
    update.mutate(
      {
        slug: server.slug,
        changes: {
          transport: {
            ...transport,
            env: {
              ...transport.env,
              ...xeroEnv({ ...credentials, ...changes }),
            },
          },
        },
      },
      { onSuccess: onSaved }
    );
  };

  return {
    credentials,
    status,
    saving: update.isPending,
    testAgain: () => void testQuery.refetch(),
    saveCredentials,
  };
}
